import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';

import {
	ICmmsAsset,
	ICmmsAssetQueryVariables,
} from '@app/pages/cmms/interfaces/assets.interfaces';
import {
	addAssetExternalData,
	addCmmsAsset,
	addCmmsAssets,
	clearAssetExternalData,
	cmmsAssetsFailedToLoad,
	cmmsAssetsLoaded,
	deleteCmmsAssetFromState,
	deleteCmmsAssets,
	dropCmmsAssetsQueryVariables,
	mapCmmsAsset,
	mapCmmsAssets,
	openAssetWorkspaceFromOutside,
	openNewAssetWorkspaceFromOutside,
	setAssetOutsideFlagForWorkspace,
	setAssetsTableConfig,
	setCmmsAsset,
	setCmmsAssetForEditing,
	setCmmsAssets,
	setEditAssetPkAndForceReload,
	startLoadingCmmsAssets,
	storeCmmsAssetsQueryVariables,
	turnOffForceReload,
	updateCmmsAsset,
	updateCmmsAssets,
	upsertCmmsAsset,
	upsertCmmsAssets,
} from './assets.actions';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { DocumentNode } from 'graphql/index';
import { AssetsQuery } from '@app/pages/cmms/graphql/assets.queries';

export interface IIfcAssetsState extends EntityState<ICmmsAsset> {
	assetEdit: Partial<ICmmsAsset> | null;
	assetsPagination: IDjangoPaginationData | null;
	assetAuPagination: IAuPaginationData | null;
	assetsQueryVariables: ICmmsAssetQueryVariables | null;
	assetIsLoading: boolean;
	assetData?: Partial<ICmmsAsset>;
	fromOutside: boolean;
	isExportable: boolean;
	tableColumnsConfig: ICMMSColumn[] | null;
	exportUrlQuery: DocumentNode | null;
	needReloadAsset: boolean;
}

export function selectAssetPK(item: ICmmsAsset): string {
	return item.pk;
}

export const assetAdapter: EntityAdapter<ICmmsAsset> = createEntityAdapter<ICmmsAsset>({
	selectId: selectAssetPK,
});

export const initialState: IIfcAssetsState = assetAdapter.getInitialState({
	// additional entity state properties
	assetEdit: null,
	assetsPagination: null,
	assetAuPagination: null,
	assetsQueryVariables: null,
	assetIsLoading: false,
	assetData: null,
	fromOutside: false,
	isExportable: false,
	tableColumnsConfig: null,
	exportUrlQuery: structuredClone(AssetsQuery),
	needReloadAsset: false,
});

export const assetReducer = createReducer(
	initialState,
	on(addCmmsAsset, (state, { asset }) => {
		return assetAdapter.addOne(asset, state);
	}),
	on(setCmmsAsset, (state, { asset }) => {
		return assetAdapter.setOne(asset, state);
	}),
	on(upsertCmmsAsset, (state, { asset }) => {
		return assetAdapter.upsertOne(asset, state);
	}),
	on(addCmmsAssets, (state, { assets }) => {
		return assetAdapter.addMany(assets, state);
	}),
	on(upsertCmmsAssets, (state, { assets }) => {
		return assetAdapter.upsertMany(assets, state);
	}),
	on(updateCmmsAsset, (state, { update }) => {
		return assetAdapter.updateOne(update, {
			...state,
			assetEdit: { ...update.changes } as ICmmsAsset,
		});
	}),
	on(updateCmmsAssets, (state, { updates }) => {
		return assetAdapter.updateMany(updates, state);
	}),
	on(mapCmmsAsset, (state, { entityMap }) => {
		return assetAdapter.mapOne(entityMap, state);
	}),
	on(mapCmmsAssets, (state, { entityMap }) => {
		return assetAdapter.map(entityMap, state);
	}),
	on(deleteCmmsAssetFromState, (state, { pk }) => {
		return assetAdapter.removeOne(pk, state);
	}),
	on(deleteCmmsAssets, (state, { pks }) => {
		return assetAdapter.removeMany(pks, state);
	}),
	on(startLoadingCmmsAssets, (state, { variables }) => {
		return {
			...state,
			assetAuPagination: {
				first: variables?.first,
				last: variables?.last,
				after: variables?.after,
				before: variables?.before,
			},
			assetIsLoading: true,
		};
	}),
	on(cmmsAssetsLoaded, (state, { assets, assetPagination }) => {
		return assetAdapter.setAll(assets, {
			...state,
			assetsPagination: assetPagination,
			assetIsLoading: false,
		});
	}),
	on(cmmsAssetsFailedToLoad, state => {
		return assetAdapter.setAll([], {
			...state,
			assetIsLoading: false,
		});
	}),
	on(setCmmsAssetForEditing, (state, { assetEdit }) => {
		return { ...state, assetEdit };
	}),
	on(setEditAssetPkAndForceReload, (state, { assetEdit }) => {
		return { ...state, assetEdit, needReloadAsset: true };
	}),
	on(turnOffForceReload, state => {
		return { ...state, needReloadAsset: false };
	}),
	on(openNewAssetWorkspaceFromOutside, (state, { assetData }) => {
		return { ...state, assetData, fromOutside: true };
	}),
	on(setCmmsAssets, (state, { assets }) => {
		return assetAdapter.setMany(assets, state);
	}),
	on(storeCmmsAssetsQueryVariables, (state, { variables }) => {
		return { ...state, assetsQueryVariables: variables };
	}),
	on(dropCmmsAssetsQueryVariables, state => {
		return { ...state, assetsQueryVariables: null };
	}),
	on(setAssetsTableConfig, (state, { isExportable, columns }) => {
		return {
			...state,
			isExportable,
			tableColumnsConfig: columns,
		};
	}),
	on(openAssetWorkspaceFromOutside, state => {
		return { ...state, fromOutside: true };
	}),
	on(setAssetOutsideFlagForWorkspace, (state, { fromOutside }) => ({
		...state,
		fromOutside,
	})),
	on(clearAssetExternalData, state => ({
		...state,
		assetData: null,
	})),

	on(addAssetExternalData, (state, { assetData }) => {
		return {
			...state,
			assetData,
		};
	})
);

const { selectIds, selectEntities, selectAll, selectTotal } = assetAdapter.getSelectors();

export const assetEntitySelector = {
	selectIds,
	selectEntities,
	selectAll,
	selectTotal,
};
